import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Box,
  Popper,
  Paper,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";

const TemplateProviderDropdown = ({
  label,
  required = false,
  value,
  options = [],
  onChange,
  onClear,
  placeholder = "Select an option",
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const open = Boolean(anchorEl);

  const filteredOptions = useMemo(() => {
    return options.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleToggle = (event) => {
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const handleSelect = (item) => {
    onChange(item);
    handleClose();
  };

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event) => {
      const clickedTrigger = triggerRef.current?.contains(event.target);
      const clickedDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedDropdown) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  return (
    <Box>
      <Typography mb={0.5} fontWeight={500}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>

      {/* Trigger */}
      <Paper
        ref={triggerRef}
        onClick={handleToggle}
        role="button"
        sx={{
          p: "10px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "#F5F7FB",
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <Typography
          color={value ? "text.primary" : "text.secondary"}
          sx={{ flex: 1 }}
        >
          {value?.name || placeholder}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {value && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
              sx={{ p: 0.5 }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          <ArrowDropDownIcon
          />
        </Box>
      </Paper>

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        disablePortal
        sx={{ zIndex: 1500 }}
      >
        <Paper ref={dropdownRef} sx={{ width: anchorEl?.offsetWidth, p: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 1 }}
          />

          <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
            {filteredOptions.length ? (
              filteredOptions.map((item) => (
                <Typography
                  key={item.id}
                  sx={{
                    p: 1,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#F0F0F0" },
                  }}
                  onClick={() => handleSelect(item)}
                >
                  {item.name}
                </Typography>
              ))
            ) : (
              <Typography p={1} color="text.secondary">
                No results found
              </Typography>
            )}
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};

export default TemplateProviderDropdown;
